import Vue from 'vue'
import axios from 'axios'
import VueAxios from 'vue-axios'
import store from '@/store/index'
import toastr from 'toastr/toastr'
import URL from 'url-parse/index'

import {
  API_SET_ERRORS,
  API_SET_WARNINGS,
  API_SET_WARN_FIELDS,
  API_SET_NOTICES,
  API_PURGE_RESULTS,
  API_SET_POSTED,
  API_SET_POST_BUTTON,
  API_SET_BUSY_INDICATOR,
  API_SET_BUSY_INTERVAL_ID,
  API_SET_BUSY_INTERVAL_TS,
  API_SET_REQUEST_BUSY,
  API_BUSY_INTERVAL_ID,
  API_REQUEST_BUSY,
  API_BUSY_INTERVAL_TS,
  API_BUSY_INDICATOR
} from '@/store/api/postStatus.types'

import {
  AUTH_GET_TOKEN,
  AUTH_SET_TOKEN
} from '@/store/auth/auth.types'

import * as reset from '@/store/reset'

export const STD_ERR_MESSAGES = {
  '400': '[400] Bad Request: We were unable to process the request. If the problem continues please contact our support team',
  '401': '[401] Unauthorized: Please try logging in again',
  '403': '[403] Forbidden: Currently you do not have access to the provided data, please contact our support team',
  '404': '[404] Not Found: The data or page you requested could not be found',
  '405': '[405] Method Not Allowed: We were unable to process the request. If the problem continues please contact our support team',
  '425': '[425] User Not Found: Please make sure you have entered your username/email address correctly',
  '419': '[419] Authentication Timeout: Your login session have expired, please try logging in again',
  '422': '[422] Unprocessable Entity: Some of the required fields have been left blank or is invalid',
  '429': '[429] Too Many Requests: Too many requests have been made to the server, please wait and try again',
  '498': '[498] Token Invalid: The login token is invalid, please try logging in again or contact our support team',
  '499': '[499] Token Required: The authorization token was not provided, please try logging in again or contact our support team',
  '500': '[500] Internal Server Error: Unfortunately a server error has occurred, we have notified our engineers'
}

// Defines which of the error codes should be seen as
// warnings as opposed to errors
export const STD_ERR_WARNINGS = [401, 403, 404, 425, 419, 422, 429, 499]
export const FORCE_LOGOUT_CODES = [401, 425, 419, 498, 499]

const ApiService = {
  init () {
    Vue.use(VueAxios, axios)
  },

  setHeader () {
    Vue.axios.defaults.headers.common['Content-Type'] = 'application/json'
    Vue.axios.defaults['responseType'] = 'json'
  },

  buildUrl (url, resource, args) {
    let result = new URL(url + resource, true)
    result.set('query', Object.assign({}, result.query, args))
    return result.href
  },

  enableRequestBusy () {
    store.commit(API_SET_REQUEST_BUSY, true)
    store.commit(API_SET_BUSY_INTERVAL_TS, new Date().getTime())

    if (store.getters[API_BUSY_INTERVAL_ID] === null) {
      const intervalId = window.setInterval(() => {
        const busyIndicator = store.getters[API_BUSY_INDICATOR]
        const requestBusy = store.getters[API_REQUEST_BUSY]
        const busyIntervalTs = store.getters[API_BUSY_INTERVAL_TS]
        const currentTime = new Date().getTime()

        if (requestBusy) {
          if (!busyIndicator) {
            if (currentTime - busyIntervalTs > 250) {
              // Busy indicator is not active yet, lets enable it now
              // since the request is still busy
              store.commit(API_SET_BUSY_INDICATOR, true)
            }
          }
        } else {
          if (busyIndicator) {
            // Request is no longer busy but the request indicator
            // is still enabled, we allow it to be enabled for another
            // x milliseconds before we shut it down, this is so that
            // follow-up requests won't cause a "flickering" effect.
            if (currentTime - busyIntervalTs > 150) {
              store.commit(API_SET_BUSY_INDICATOR, false)
              window.clearInterval(store.getters[API_BUSY_INTERVAL_ID])
              store.commit(API_SET_BUSY_INTERVAL_TS, null)
              store.commit(API_SET_BUSY_INTERVAL_ID, null)
            }
          } else {
            window.clearInterval(store.getters[API_BUSY_INTERVAL_ID])
            store.commit(API_SET_BUSY_INTERVAL_TS, null)
            store.commit(API_SET_BUSY_INTERVAL_ID, null)
          }
        }
      }, 50)

      store.commit(API_SET_BUSY_INTERVAL_ID, intervalId)
    }
  },

  disableRequestBusy () {
    store.commit(API_SET_REQUEST_BUSY, false)
    store.commit(API_SET_BUSY_INTERVAL_TS, new Date().getTime())
  },

  handleError (response, autoLogout = true) {
    console.log(response)
    this.disableRequestBusy()
    let errors = []
    let warnings = []
    let warn_fields = []
    if (response.headers['content-type'] === 'application/json') {
      if (response.hasOwnProperty('data')) {
        if (response.data.hasOwnProperty('errors')) {
          errors = response.data.errors
        }

        if (response.data.hasOwnProperty('warnings')) {
          warnings = response.data.warnings
        }

        if (response.data.hasOwnProperty('warn_fields')) {
          warn_fields = response.data.warn_fields
        }
      }
    }

    if (errors.length === 0 && warnings.length === 0 && warn_fields.length === 0) {
      if (STD_ERR_MESSAGES.hasOwnProperty(response.status.toString())) {
        if (STD_ERR_WARNINGS.includes(response.status)) {
          warnings.push(STD_ERR_MESSAGES[response.status])
        } else {
          errors.push(STD_ERR_MESSAGES[response.status])
        }
      } else {
        errors.push(`Error ${response.status}: An unknown error occurred, please contact our support team`)
      }
    }

    store.commit(API_SET_ERRORS, errors)
    store.commit(API_SET_WARNINGS, warnings)
    store.commit(API_SET_WARN_FIELDS, warn_fields)

    errors.slice(0, 4).forEach((error) => {
      toastr.error(error, 'Error', {
        'closeButton': true,
        'timeOut': 0,
        'extendedTimeOut': 0
      })
    })

    warnings.slice(0, 4).forEach((warning) => {
      toastr.warning(warning, 'Warning', {
        timeOut: 5000,
        extendedTimeOut: 1000,
        progressBar: true
      })
    })

    if (warn_fields.length > 0) {
      let html = ['<ul>']
      warn_fields.slice(0, 4).forEach((warn_field) => {
        let fieldname = Object.keys(warn_field)[0]
        let warning = warn_field[fieldname]
        html.push(`<li>${warning}</li>`)
      })
      html.push('</ul>')

      toastr.warning(html.join(''), 'Warning', {
        timeOut: 5000,
        extendedTimeOut: 1000,
        progressBar: true
      })
    }

    if (autoLogout && FORCE_LOGOUT_CODES.includes(response.status)) {
      reset.resetAll()
    }

    store.commit(API_SET_POST_BUTTON, null)
  },

  handleSuccess (response, refreshToken = true) {
    // Update the authorization token if we received a new one
    if (refreshToken && response.headers.hasOwnProperty('authorization')) {
      store.commit(AUTH_SET_TOKEN, response.headers['authorization'])
    }

    this.disableRequestBusy()

    if (response.hasOwnProperty('data')) {
      if (response.data.hasOwnProperty('notices')) {
        response.data.notices.forEach((notice) => {
          toastr.success(notice, 'Success')
        })

        store.commit(API_SET_NOTICES, response.data.notices)
      }
    }

    store.commit(API_SET_POST_BUTTON, null)

    return response
  },

  getAxiosConfig (authToken = null) {
    if (authToken === null && store.getters[AUTH_GET_TOKEN] !== null) {
      return {
        headers: {
          Authorization: store.getters[AUTH_GET_TOKEN]
        }
      }
    } else if (authToken !== null) {
      return {
        headers: {
          Authorization: authToken
        }
      }
    }

    return {}
  },

  get (resource, params = {}, url = null, authToken = null) {
    this.enableRequestBusy()
    this.setHeader()

    if (process.env.NODE_ENV === 'development') {
      params['XDEBUG_SESSION_START'] = 'PHPSTORM'
    }

    const fullUrl = url !== null
      ? this.buildUrl(url, resource, params)
      : this.buildUrl(process.env.API_URL, resource, params)

    const config = this.getAxiosConfig(authToken)
    return Vue.axios.get(fullUrl, config)
      .then((response) => {
        console.info('[Destiny] ApiService: Get Success')
        return this.handleSuccess(response, authToken === null)
      })
      .catch(({ response }) => {
        console.error('[Destiny] ApiService: Get Error')
        this.handleError(response, authToken === null)

        // Allow requests further down the chain to handle the rest of
        // the error.
        throw new Error(`[Destiny] ApiService: ${response.status} ${response.statusText}`)
      })
  },

  /**
   * POST's data to the API. The payload can be specified in
   * two formats:
   * a) A simple Object containing the data.
   * b) An object with the structure { data: {}, params: {} }
   *
   * Option b) is to allow sending URL arguments together
   * with the data.
   *
   * @param resource String
   * @param payload Object
   * @param url String
   * @param authToken String
   */
  post (resource, payload = {}, url = null, authToken = null) {
    this.enableRequestBusy()
    store.commit(API_PURGE_RESULTS)
    this.setHeader()

    const config = this.getAxiosConfig(authToken)
    let params = {}
    let data = {}
    if (payload.hasOwnProperty('data')) {
      data = payload.data
      params = payload.params
    } else {
      data = payload
    }

    if (process.env.NODE_ENV === 'development') {
      params['XDEBUG_SESSION_START'] = 'PHPSTORM'
    }

    const fullUrl = url !== null
      ? this.buildUrl(url, resource, params)
      : this.buildUrl(process.env.API_URL, resource, params)

    return Vue.axios.post(fullUrl, data, config)
      .then((response) => {
        console.info('[Destiny] ApiService: Post Success')
        store.commit(API_SET_POSTED, true)
        return this.handleSuccess(response, authToken === null)
      })
      .catch(({ response }) => {
        // A note about using the response parameter directly
        // vs destructuring the response using {response}:
        //
        // If the response is not destructured, it contains the
        // the javascript exception, which is useful if you want
        // to re-raise the exception. However, you then also need
        // to manually decode the error using something like:
        // let response = JSON.parse(JSON.stringify(error))
        // If you destructure it then it contains the actual
        // response, but then you cannot re-throw the exception.
        //
        // Please see: https://github.com/axios/axios/issues/960
        // And: https://github.com/axios/axios#handling-errors

        console.error('[Destiny] ApiService: Post Error')
        store.commit(API_SET_POSTED, true)
        this.handleError(response, authToken === null)

        // Allow requests further down the chain to handle the rest of
        // the error.
        throw new Error(`[Destiny] ApiService: ${response.status} ${response.statusText}`)
      })
  },

  /**
   * PUT's data to the API. The payload can be specified in
   * two formats:
   * a) A simple Object containing the data.
   * b) An object with the structure { data: {}, params: {} }
   *
   * Option b) is to allow sending URL arguments together
   * with the data.
   *
   * @param resource String
   * @param payload Object
   * @param url String
   * @param authToken String
   */
  put (resource, payload = {}, url = null, authToken = null) {
    this.enableRequestBusy()
    store.commit(API_PURGE_RESULTS)
    this.setHeader()

    const config = this.getAxiosConfig(authToken)
    let params = {}
    let data = {}
    if (payload.hasOwnProperty('data')) {
      data = payload.data
      params = payload.params
    } else {
      data = payload
    }

    if (process.env.NODE_ENV === 'development') {
      params['XDEBUG_SESSION_START'] = 'PHPSTORM'
    }
    const fullUrl = url !== null
      ? this.buildUrl(url, resource, params)
      : this.buildUrl(process.env.API_URL, resource, params)

    return Vue.axios.put(fullUrl, data, config)
      .then((response) => {
        console.info('[Destiny] ApiService: Put Success')
        store.commit(API_SET_POSTED, true)
        return this.handleSuccess(response, authToken === null)
      })
      .catch(({ response }) => {
        console.error('[Destiny] ApiService: Put Error')
        store.commit(API_SET_POSTED, true)
        this.handleError(response, authToken === null)

        // Allow requests further down the chain to handle the rest of
        // the error.
        throw new Error(`[Destiny] ApiService: ${response.status} ${response.statusText}`)
      })
  },

  delete (resource, params = {}, url = null, authToken = null) {
    this.enableRequestBusy()
    store.commit(API_PURGE_RESULTS)
    this.setHeader()

    const config = this.getAxiosConfig(authToken)
    if (process.env.NODE_ENV === 'development') {
      params['XDEBUG_SESSION_START'] = 'PHPSTORM'
    }
    const fullUrl = url !== null
      ? this.buildUrl(url, resource, params)
      : this.buildUrl(process.env.API_URL, resource, params)

    return Vue.axios.delete(fullUrl, config)
      .then((response) => {
        console.info('[Destiny] ApiService: Delete Success')
        return this.handleSuccess(response, authToken === null)
      })
      .catch(({ response }) => {
        console.error('[Destiny] ApiService: Delete Error')
        this.handleError(response, authToken === null)

        // Allow requests further down the chain to handle the rest of
        // the error.
        throw new Error(`[Destiny] ApiService: ${response.status} ${response.statusText}`)
      })
  },

  /**
   * Decodes a JWT token string and return it as an object.
   *
   * @param token
   * @returns Object
   */
  decodeJwt (token) {
    let base64Url = token.split('.')[1]
    let base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
    let jsonPayload = decodeURIComponent(atob(base64).split('').map((c) => {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
    }).join(''))

    return JSON.parse(jsonPayload)
  }
}

export default ApiService



// WEBPACK FOOTER //
// ./src/common/api.service.js